import { Controller } from '@hotwired/stimulus';
import { installWorkers, loaded, themeName, watchTheme } from '../lib/monaco';

const NARROW = '(max-width: 767px)';

const OPTIONS = {
  automaticLayout: true,
  fontFamily: 'SF Mono, Menlo, monospace',
  fontSize: 13,
  minimap: { enabled: false },
  overviewRulerLanes: 0,
  padding: { top: 12 },
  readOnly: true,
  renderOverviewRuler: false,
  scrollBeyondLastLine: false,
};

export default class extends Controller {
  static values = { workers: Object };

  initialize() {
    this.models = new Map();
    this.ready = this.#setup();
  }

  disconnect() {
    this.ready.then(() => this.#teardown());
  }

  async show(path, before, after, language) {
    const monaco = await this.ready;

    this.editor.setModel(this.#model(monaco, path, before, after, language));
  }

  async #setup() {
    installWorkers(this.workersValue);

    const monaco = await loaded;

    this.host = document.createElement('div');
    this.host.className = 'h-full';
    this.element.replaceChildren(this.host);

    this.narrow = window.matchMedia(NARROW);

    this.editor = monaco.editor.createDiffEditor(this.host, {
      ...OPTIONS,
      renderSideBySide: !this.narrow.matches,
      theme: themeName(),
    });

    this.layout = () => this.editor.updateOptions({ renderSideBySide: !this.narrow.matches });
    this.narrow.addEventListener('change', this.layout);

    this.observer = watchTheme(monaco);

    this.dispatch('ready');

    return monaco;
  }

  #teardown() {
    this.narrow.removeEventListener('change', this.layout);
    this.observer.disconnect();
    this.editor.dispose();
    this.models.forEach(({ original, modified }) => {
      original.dispose();
      modified.dispose();
    });
    this.models.clear();
  }

  #model(monaco, path, before, after, language) {
    if (!this.models.has(path)) {
      this.models.set(path, {
        original: monaco.editor.createModel(before, language),
        modified: monaco.editor.createModel(after, language),
      });
    }

    return this.models.get(path);
  }
}
